import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminRole, PromoCode } from "../api/types.js";
import { MutationForm, fieldError } from "../forms/mutation-form.js";
import { fromUtcInput, utc } from "../format.js";
import { feePercent, percentToBps } from "./prices.js";
import { VersionedPanel } from "./versioned-panel.js";
import { Blank, Button, ErrorNote, Field, inputClass, Pill, ScreenHeader, ShortId, Spinner, Table, Td, Th } from "../ui.js";

/**
 * Promo codes: a fee in force for `duration_days` from the moment an account redeems the code.
 *
 * The lower of the promo fee and the account's tier fee wins, and an account holds one promo
 * at a time (routing-fee-tiers-spec.md). A code is never deleted — it is deactivated, because
 * the redemptions that point at it keep their window.
 */

const CODE_PATTERN = /^[A-Z0-9-]{4,32}$/;

export function PromoCodesScreen({ role }: { role: AdminRole }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const list = useResource((signal) => {
    void signal;
    return adminReads.promoCodes({ limit: 200 });
  }, []);
  const rows = list.data?.items ?? [];

  const toggle = (row: PromoCode) => {
    setBusy(row.id);
    void adminWrites
      .updatePromoCode(row.id, { active: !row.active })
      .then(() => list.reload())
      .finally(() => setBusy(null));
  };

  return (
    <>
      <ScreenHeader
        title="Promo codes"
        lead="A code sets the routing fee for a number of days after an account redeems it. The lower of promo and tier fee wins; a new code replaces the account's old one."
      />
      <VersionedPanel
        title="Codes"
        subtitle="Newest first. Uses count redemptions; a deactivated code refuses new ones and leaves running windows alone."
        role={role}
        formLabel="Create a promo code"
        renderForm={(done, cancel) => (
          <PromoCodeForm
            onDone={() => {
              list.reload();
              done();
            }}
            onCancel={cancel}
          />
        )}
      >
        {list.loading ? (
          <Spinner />
        ) : list.error !== null ? (
          <ErrorNote error={list.error} onRetry={() => list.reload()} />
        ) : (
          <Table
            rows={rows.length}
            empty="No promo code has been created yet."
            head={
              <tr>
                <Th>Code</Th>
                <Th numeric>Fee</Th>
                <Th numeric>Days</Th>
                <Th>Window</Th>
                <Th numeric>Uses / max</Th>
                <Th>State</Th>
                <Th>Note</Th>
                <Th>{""}</Th>
              </tr>
            }
          >
            {rows.map((row) => [
              <tr key={row.id}>
                <Td mono>{row.code}</Td>
                <Td numeric>
                  <span title={`${row.fee_bps} bps`}>{feePercent(row.fee_bps)}</span>
                </Td>
                <Td numeric>{row.duration_days}</Td>
                <Td mono>
                  {utc(row.starts_at)} → {row.ends_at === null ? "no end" : utc(row.ends_at)}
                </Td>
                <Td numeric>
                  {row.uses} / {row.max_uses === null ? "∞" : row.max_uses}
                </Td>
                <Td>
                  <Pill value={row.active ? "active" : "inactive"} tone={row.active ? "good" : "neutral"} />
                </Td>
                <Td>{row.note === "" ? <Blank /> : row.note}</Td>
                <Td>
                  <div class="flex justify-end gap-2">
                    <Button tone="ghost" onClick={() => setOpen(open === row.id ? null : row.id)}>
                      {open === row.id ? "Hide redemptions" : "Redemptions"}
                    </Button>
                    {role === "editor" ? (
                      <Button
                        tone={row.active ? "danger" : "primary"}
                        disabled={busy === row.id}
                        onClick={() => toggle(row)}
                      >
                        {row.active ? "Deactivate" : "Activate"}
                      </Button>
                    ) : null}
                  </div>
                </Td>
              </tr>,
              open === row.id ? (
                <tr key={`${row.id}-redemptions`}>
                  <td colSpan={8} class="px-4 pb-3">
                    <Redemptions id={row.id} />
                  </td>
                </tr>
              ) : null,
            ])}
          </Table>
        )}
      </VersionedPanel>
    </>
  );
}

function Redemptions({ id }: { id: string }) {
  const list = useResource(
    (signal) => {
      void signal;
      return adminReads.promoRedemptions(id);
    },
    [id],
  );
  if (list.loading) return <Spinner />;
  if (list.error !== null) return <ErrorNote error={list.error} onRetry={() => list.reload()} />;
  const rows = list.data?.items ?? [];
  return (
    <Table
      rows={rows.length}
      empty="Nobody has redeemed this code yet."
      head={
        <tr>
          <Th>Account</Th>
          <Th>Redeemed</Th>
          <Th>Expires</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td>
            <ShortId value={row.account_id} />
          </Td>
          <Td mono>{utc(row.redeemed_at)}</Td>
          <Td mono>{utc(row.expires_at)}</Td>
        </tr>
      ))}
    </Table>
  );
}

function PromoCodeForm({ onDone, onCancel }: { onDone(): void; onCancel(): void }) {
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState("");
  const [days, setDays] = useState("30");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const normalized = code.trim().toUpperCase();
  const codeOk = normalized === "" || CODE_PATTERN.test(normalized);
  const feeBps = percentToBps(percent);
  const durationDays = Number(days);
  const daysOk = Number.isInteger(durationDays) && durationDays >= 1;
  const uses = maxUses.trim() === "" ? null : Number(maxUses);
  const usesOk = uses === null || (Number.isInteger(uses) && uses >= 1);
  const valid = codeOk && feeBps !== null && daysOk && usesOk;

  return (
    <MutationForm
      title="Create a promo code"
      description="The fee applies for the given days from the moment an account redeems the code. The note is the audit reason."
      changes={[
        { label: "code", before: null, after: normalized === "" ? "generated (8 chars)" : normalized },
        { label: "fee_bps", before: null, after: feeBps === null ? "—" : `${feeBps} (${feePercent(feeBps)})` },
        { label: "duration_days", before: null, after: daysOk ? String(durationDays) : "—" },
        { label: "max_uses", before: null, after: uses === null ? "unlimited" : String(uses) },
      ]}
      effect={startsAt === "" ? "Redeemable at once." : `Redeemable from ${startsAt.replace("T", " ")} UTC.`}
      submitLabel="Create promo code"
      busy={busy}
      disabled={!valid}
      error={error}
      reason={note}
      onReason={setNote}
      onCancel={onCancel}
      onSubmit={() => {
        if (!valid || feeBps === null) return;
        setBusy(true);
        setError(null);
        const starts = fromUtcInput(startsAt);
        const ends = fromUtcInput(endsAt);
        void adminWrites
          .createPromoCode({
            ...(normalized === "" ? {} : { code: normalized }),
            fee_bps: feeBps,
            duration_days: durationDays,
            ...(starts === undefined ? {} : { starts_at: starts }),
            ...(ends === undefined ? {} : { ends_at: ends }),
            ...(uses === null ? {} : { max_uses: uses }),
            note,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field
        label="Code (optional)"
        hint="Leave empty to generate 8 characters. 4 to 32 of A-Z, 0-9 and -; typed in upper case."
        error={fieldError(error, "code") ?? (codeOk ? null : "4 to 32 of A-Z, 0-9 and -.")}
      >
        <input
          class={inputClass}
          name="code"
          value={code}
          onInput={(event) => setCode((event.currentTarget as HTMLInputElement).value.toUpperCase())}
        />
      </Field>
      <Field
        label="Fee (%)"
        hint="0 to 20, step 0.1. 3 = 300 bps."
        error={
          fieldError(error, "fee_bps") ??
          (percent !== "" && feeBps === null ? "A percent from 0 to 20 in 0.1 steps." : null)
        }
      >
        <input
          class={inputClass}
          name="fee_percent"
          type="number"
          min={0}
          max={20}
          step={0.1}
          value={percent}
          onInput={(event) => setPercent((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Days after redemption"
        error={fieldError(error, "duration_days") ?? (daysOk ? null : "A whole number of days, 1 or more.")}
      >
        <input
          class={inputClass}
          name="duration_days"
          type="number"
          min={1}
          step={1}
          value={days}
          onInput={(event) => setDays((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Starts (UTC, optional)" hint="Empty = at once." error={fieldError(error, "starts_at")}>
        <input
          class={inputClass}
          name="starts_at"
          type="datetime-local"
          value={startsAt}
          onInput={(event) => setStartsAt((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Ends (UTC, optional)" hint="Empty = no end." error={fieldError(error, "ends_at")}>
        <input
          class={inputClass}
          name="ends_at"
          type="datetime-local"
          value={endsAt}
          onInput={(event) => setEndsAt((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Max uses (optional)"
        hint="Empty = unlimited."
        error={fieldError(error, "max_uses") ?? (usesOk ? null : "A whole number, 1 or more.")}
      >
        <input
          class={inputClass}
          name="max_uses"
          type="number"
          min={1}
          step={1}
          value={maxUses}
          onInput={(event) => setMaxUses((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}
